import React, { useState, useEffect, useRef } from "react";
import "../css/NewsNoti.css";
import { Skeleton, Spin, Empty } from "antd";
import useCustom from "../customHook/customHook";
import * as hasura from "../service/hasura";

const PAGE_SIZE = 50;

const emoji = {
  เหตุฉุกเฉิน: "🚨❗",
  ภัยพิบัติ: "🏚🌪",
  น้ำท่วม: "☔🌧",
  ก่อสร้าง: "🚧🏗",
  ผลงานและความสำเร็จ: "🎉🎊",
  ผลงาน: "🎉🎊",
  ข่าวสาร: "📢📰",
  เหตุเพลิงไหม้: "🔥🚒",
};

const formatNewsTime = (createdAt) => {
  if (!createdAt) return { time_show: "", data_time: "" };
  const now = new Date();
  let targetDate00 = new Date(createdAt);
  let utcTime = targetDate00.getTime();
  let gmtPlus07Time = utcTime + 7 * 60 * 60 * 1000;
  let targetDate = new Date(gmtPlus07Time);
  let timeDifference = now - targetDate;
  let years = targetDate.getFullYear() - now.getFullYear();
  let days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  let hours = Math.floor(
    (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  let minutes = Math.floor(
    (timeDifference % (1000 * 60 * 60)) / (1000 * 60)
  );
  let seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);
  let time_show = "";

  if (years > 0) time_show = `${years} ปีที่แล้ว`;
  else if (days > 0) time_show = `${days} วันที่แล้ว`;
  else if (hours > 0) time_show = `${hours} ชั่วโมงที่แล้ว`;
  else if (minutes > 0) time_show = `${minutes} นาทีที่แล้ว`;
  else if (seconds > 0) time_show = `${seconds} วินาทีที่แล้ว`;
  else time_show = "เมื่อสักครู่";

  const options = {
    hour: "2-digit",
    minute: "2-digit",
    day: "numeric",
    month: "long",
    year: "numeric",
    era: undefined,
  };
  const thaiLocale = "th-TH";
  const formatter = new Intl.DateTimeFormat(thaiLocale, options);
  const formattedDateParts = formatter.formatToParts(targetDate);
  const formattedTime = `${formattedDateParts[6]?.value || ""}:${formattedDateParts[8]?.value || ""}`;
  const formattedDate = `${formattedDateParts[0]?.value || ""} ${formattedDateParts[2]?.value || ""} ${formattedDateParts[4]?.value || ""}`;
  const data_time = `${formattedTime} · ${formattedDate}`;

  return { time_show, data_time };
};

export default function NewsNoti(props) {
  const [globalState, setGlobalState] = useCustom();
  const [currentSelected, setCurrentSelected] = useState("total"); // "total" | "unread"

  // Tab 1: Personal notifications state
  const [totalNews, setTotalNews] = useState([]);
  const [isLoadingTotal, setIsLoadingTotal] = useState(true);
  const [isLoadingMoreTotal, setIsLoadingMoreTotal] = useState(false);
  const [hasMoreTotal, setHasMoreTotal] = useState(true);

  // Tab 2: All news catalog state
  const [allNews, setAllNews] = useState([]);
  const [isLoadingAll, setIsLoadingAll] = useState(true);
  const [isLoadingMoreAll, setIsLoadingMoreAll] = useState(false);
  const [hasMoreAll, setHasMoreAll] = useState(true);

  // Detail view state
  const [newsDetail, setNewsDetail] = useState(null);
  const [isOpenDetail, setIsOpenDetail] = useState(false);

  // Ref to always have latest state in window scroll listener
  const scrollStateRef = useRef({});
  scrollStateRef.current = {
    currentSelected,
    isOpenDetail,
    hasMoreTotal,
    isLoadingTotal,
    isLoadingMoreTotal,
    totalNewsCount: totalNews.length,
    hasMoreAll,
    isLoadingAll,
    isLoadingMoreAll,
    allNewsCount: allNews.length,
    lineUserId: globalState.line_user_id,
  };

  const loadInitialTotalNews = async (lineUserId) => {
    setIsLoadingTotal(true);
    try {
      const res = await hasura.getAllNews(lineUserId, {
        limit: PAGE_SIZE,
        offset: 0,
      });
      const items = res.traffy_user?.[0]?.fondue_alert_notifications || [];
      if (!globalState.traffy_user_id && res.traffy_user?.[0]?.traffy_user_id) {
        setGlobalState({ traffy_user_id: res.traffy_user[0].traffy_user_id });
      }
      setTotalNews(items);
      setHasMoreTotal(items.length === PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load personal notifications:", err);
    } finally {
      setIsLoadingTotal(false);
    }
  };

  const loadMoreTotalNews = async () => {
    const s = scrollStateRef.current;
    if (s.isLoadingMoreTotal || !s.hasMoreTotal) return;

    setIsLoadingMoreTotal(true);
    try {
      const res = await hasura.getAllNews(s.lineUserId, {
        limit: PAGE_SIZE,
        offset: s.totalNewsCount,
      });
      const newItems = res.traffy_user?.[0]?.fondue_alert_notifications || [];
      setTotalNews((prev) => [...prev, ...newItems]);
      setHasMoreTotal(newItems.length === PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load more personal notifications:", err);
    } finally {
      setIsLoadingMoreTotal(false);
    }
  };

  const loadInitialAllNews = async (lineUserId) => {
    setIsLoadingAll(true);
    try {
      const res = await hasura.getAllOfNews(lineUserId, {
        limit: PAGE_SIZE,
        offset: 0,
      });
      const items = res.news || [];
      setAllNews(items);
      setHasMoreAll(items.length === PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load all news:", err);
    } finally {
      setIsLoadingAll(false);
    }
  };

  const loadMoreAllNews = async () => {
    const s = scrollStateRef.current;
    if (s.isLoadingMoreAll || !s.hasMoreAll) return;

    setIsLoadingMoreAll(true);
    try {
      const res = await hasura.getAllOfNews(s.lineUserId, {
        limit: PAGE_SIZE,
        offset: s.allNewsCount,
      });
      const newItems = res.news || [];
      setAllNews((prev) => [...prev, ...newItems]);
      setHasMoreAll(newItems.length === PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load more news:", err);
    } finally {
      setIsLoadingMoreAll(false);
    }
  };

  useEffect(() => {
    loadInitialTotalNews(globalState.line_user_id);
    loadInitialAllNews(globalState.line_user_id);
  }, [globalState.line_user_id]);

  // Infinite Scroll Listener
  useEffect(() => {
    const handleScroll = () => {
      const s = scrollStateRef.current;
      if (!s || s.isOpenDetail) return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight =
        window.innerHeight || document.documentElement.clientHeight;

      // Trigger next page when within 300px of bottom
      if (scrollHeight - (scrollTop + clientHeight) < 300) {
        if (s.currentSelected === "total") {
          if (s.hasMoreTotal && !s.isLoadingTotal && !s.isLoadingMoreTotal) {
            loadMoreTotalNews();
          }
        } else {
          if (s.hasMoreAll && !s.isLoadingAll && !s.isLoadingMoreAll) {
            loadMoreAllNews();
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleChangeMenu = (menu) => {
    setCurrentSelected(menu);
  };

  const renderSkeletonList = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "12px" }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="container-news" style={{ padding: "8px 0" }}>
          <div
            className="container-image"
            style={{ width: "25%", display: "flex", justifyContent: "center" }}
          >
            <Skeleton.Avatar active shape="square" size={68} />
          </div>
          <div className="container-info" style={{ width: "65%" }}>
            <Skeleton active paragraph={{ rows: 2 }} title={{ width: "80%" }} />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {isOpenDetail && newsDetail ? (
        <div className="container-detail">
          <div className="container-detail-header">
            <div
              style={{
                width: "15%",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => {
                setGlobalState({ isShowBanner: true });
                setIsOpenDetail(false);
                setNewsDetail(null);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="21"
                height="21"
                viewBox="0 0 21 21"
                fill="none"
              >
                <mask
                  id="mask0_221_441"
                  mask-type="alpha"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="21"
                  height="21"
                >
                  <rect
                    x="0.585938"
                    y="0.918945"
                    width="20"
                    height="20"
                    fill="#D9D9D9"
                  />
                </mask>
                <g mask="url(#mask0_221_441)">
                  <path
                    d="M13.9193 19.2526L5.58594 10.9193L13.9193 2.58594L15.3984 4.0651L8.54427 10.9193L15.3984 17.7734L13.9193 19.2526Z"
                    fill="#1C1B1F"
                    fillOpacity="0.5"
                    style={{ marginRight: 1 + "em" }}
                  />
                </g>
              </svg>
              <span className="text-back">กลับ</span>
            </div>
            <div
              style={{
                width: "70%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span className="text-header">
                {emoji[newsDetail.typeName] || "📢"} {newsDetail.title}
              </span>
            </div>
            <div style={{ width: "15%" }}></div>
          </div>
          <div className="container-detail-image">
            <img
              style={{ width: "60%", height: "auto", borderRadius: "8px" }}
              src={newsDetail.photo}
              alt={newsDetail.title}
            />
          </div>
          <div className="container-detail-content">
            <span className="text-detail-content">{newsDetail.desription}</span>
            {newsDetail.url && newsDetail.url.length > 0 && (
              <span
                className="text-detail-url"
                onClick={() => {
                  window.open(newsDetail.url, "_blank");
                }}
              >
                {newsDetail.url}
              </span>
            )}
          </div>
          <div className="container-detail-footer">
            <span style={{ display: "flex", flexDirection: "column" }}>
              <span className="text-footer">{newsDetail.time}</span>
              <span className="text-footer">{newsDetail.data_time}</span>
            </span>
            <span className="text-footer">{newsDetail.orgName}</span>
          </div>
        </div>
      ) : (
        <div className="container-noti">
          <div className="container-header">
            <div
              className={currentSelected === "total" ? "total-news" : "unread-news"}
              id="total"
              onClick={() => handleChangeMenu("total")}
              style={{ cursor: "pointer" }}
            >
              <span
                className={
                  currentSelected === "total"
                    ? "total-news-text"
                    : "unread-news-text"
                }
                id="text-total"
              >
                แจ้งเตือน
              </span>
            </div>
            <div
              className={currentSelected === "unread" ? "total-news" : "unread-news"}
              id="unread"
              style={{ marginLeft: "8px", cursor: "pointer" }}
              onClick={() => handleChangeMenu("unread")}
            >
              <span
                className={
                  currentSelected === "unread"
                    ? "total-news-text"
                    : "unread-news-text"
                }
                id="text-unread"
              >
                ทั้งหมด
              </span>
            </div>
          </div>

          <div className="container-content">
            {currentSelected === "total" ? (
              <>
                {isLoadingTotal ? (
                  renderSkeletonList()
                ) : totalNews.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "48px 16px" }}>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="ยังไม่มีการแจ้งเตือน"
                    />
                  </div>
                ) : (
                  <>
                    {totalNews.map((item) => {
                      const news = item.news || {};
                      const typeName = news.news_type?.name || "";
                      const orgName = news.voice_fonduegroup?.name || "";
                      const { time_show, data_time } = formatNewsTime(
                        news.created_at
                      );
                      const isRead = item.read;

                      return (
                        <div
                          className="container-news"
                          key={item.notification_id || item.id}
                          onClick={() => {
                            setGlobalState({ isShowBanner: false });
                            setIsOpenDetail(true);
                            setNewsDetail({
                              ...news,
                              typeName,
                              orgName,
                              time: time_show,
                              data_time,
                            });
                            if (!isRead) {
                              setTotalNews((prev) =>
                                prev.map((n) =>
                                  n.notification_id === item.notification_id
                                    ? { ...n, read: true }
                                    : n
                                )
                              );
                              hasura.updateNews(
                                globalState.traffy_user_id,
                                news.id
                              );
                            }
                          }}
                        >
                          <div className="container-image">
                            <img
                              style={{
                                width: "80%",
                                height: "auto",
                                borderRadius: "8px",
                              }}
                              src={news.photo}
                              alt={news.title}
                            />
                          </div>
                          <div className="container-info">
                            <span
                              className={
                                isRead
                                  ? "info-text-header-already"
                                  : "info-text-header"
                              }
                            >
                              {emoji[typeName] || "📢"} {news.title}
                            </span>
                            <span
                              className={
                                isRead
                                  ? "info-text-title-already"
                                  : "info-text-title"
                              }
                            >
                              {news.desription}
                            </span>
                            <span
                              className={
                                isRead
                                  ? "info-text-time-already"
                                  : "info-text-time"
                              }
                            >
                              {time_show} โดย {orgName}
                            </span>
                          </div>
                          <div className="container-status">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="11"
                              height="11"
                              viewBox="0 0 11 11"
                              fill="none"
                            >
                              <circle
                                cx="5.93115"
                                cy="5.91895"
                                r="5"
                                fill={isRead ? "#FFFFFF" : "#70C303"}
                              />
                            </svg>
                          </div>
                        </div>
                      );
                    })}

                    {isLoadingMoreTotal && (
                      <div style={{ textAlign: "center", padding: "16px 0" }}>
                        <Spin tip="กำลังโหลดเพิ่มเติม..." size="small" />
                      </div>
                    )}

                    {!hasMoreTotal && totalNews.length >= 10 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "16px 0",
                          color: "rgba(0,0,0,0.40)",
                          fontSize: "13px",
                        }}
                      >
                        แสดงรายการทั้งหมดแล้ว
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {isLoadingAll ? (
                  renderSkeletonList()
                ) : allNews.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "48px 16px" }}>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="ยังไม่มีข่าวสาร"
                    />
                  </div>
                ) : (
                  <>
                    {allNews.map((news) => {
                      const typeName = news.news_news_type?.name || "";
                      const orgName = news.news_voice_fonduegroup?.name || "";
                      const { time_show, data_time } = formatNewsTime(
                        news.created_at
                      );

                      return (
                        <div
                          className="container-news"
                          key={news.id}
                          onClick={() => {
                            setGlobalState({ isShowBanner: false });
                            setIsOpenDetail(true);
                            setNewsDetail({
                              ...news,
                              typeName,
                              orgName,
                              time: time_show,
                              data_time,
                            });
                          }}
                        >
                          <div className="container-image">
                            <img
                              style={{
                                width: "80%",
                                height: "auto",
                                borderRadius: "8px",
                              }}
                              src={news.photo}
                              alt={news.title}
                            />
                          </div>
                          <div className="container-info">
                            <span className="info-text-header-already">
                              {emoji[typeName] || "📢"} {news.title}
                            </span>
                            <span className="info-text-title-already">
                              {news.desription}
                            </span>
                            <span className="info-text-time-already">
                              {time_show} โดย {orgName}
                            </span>
                          </div>
                          <div className="container-status">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="11"
                              height="11"
                              viewBox="0 0 11 11"
                              fill="none"
                            >
                              <circle
                                cx="5.93115"
                                cy="5.91895"
                                r="5"
                                fill="#FFFFFF"
                              />
                            </svg>
                          </div>
                        </div>
                      );
                    })}

                    {isLoadingMoreAll && (
                      <div style={{ textAlign: "center", padding: "16px 0" }}>
                        <Spin tip="กำลังโหลดเพิ่มเติม..." size="small" />
                      </div>
                    )}

                    {!hasMoreAll && allNews.length >= 10 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "16px 0",
                          color: "rgba(0,0,0,0.40)",
                          fontSize: "13px",
                        }}
                      >
                        แสดงรายการทั้งหมดแล้ว
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
